import { Types } from "../elements";
import { bounds as mbBounds, viewport } from "@mapbox/geo-viewport";
export type LngLat = [number, number];
type Location = Types["GeoLocation"] | number[];
export type MapboxCamera = Types["GeoCamera"];

export const DEFAULT_ZOOM = 13.5;
export const DEFAULT_MAPSTYLE = "mapbox://styles/mapbox/streets-v11";
export const DEFAULT_MAPSTYLE_DARK = "mapbox://styles/mapbox/dark-v10";

export const DEFAULT_STYLE = 13.5;

export const DEFAULT_VIEWPORT = [320, 480];

export function setDefaultGeoViewport(width: number, height: number) {
  DEFAULT_VIEWPORT[0] = width;
  DEFAULT_VIEWPORT[1] = height;
}

export interface PointFeature {
  type: "Point";
  coordinates: [number, number];
}

export interface CameraBounds {
  ne: [number, number];
  sw: [number, number];
  paddingLeft?: number;
  paddingRight?: number;
  paddingTop?: number;
  paddingBottom?: number;
}

export interface HeadingUpdate {
  heading: number;
  timestamp: number;
}

/* Convert any location type to a LngLat coordinat */
export function resolveCoordinate(value: Location): LngLat;
export function resolveCoordinate(
  value: Location | undefined
): LngLat | undefined;
export function resolveCoordinate(
  value: Location | undefined
): LngLat | undefined {
  if (!value) return undefined;

  if ("lat" in value) {
    return [value.lng, value.lat];
  } else {
    return value as LngLat;
  }
}

/**
 * Convert any location type to a GeoLocation.
 */
export function resolveLocation(value: Location): Types["GeoLocation"];
export function resolveLocation(
  value: Location | undefined
): Types["GeoLocation"] | undefined;
export function resolveLocation(value: Location | undefined) {
  if (typeof value === "undefined") return undefined;

  if ("lat" in value) {
    return value;
  } else {
    return {
      lng: value[0],
      lat: value[1],
    };
  }
}

export function centerCoordinateToBounds(
  coordinate: number[] | Types["GeoLocation"],
  zoom = DEFAULT_ZOOM,
  viewport = DEFAULT_VIEWPORT
): Types["GeoBounds"] {
  let coord: number[];
  if ("lat" in coordinate) {
    coord = [coordinate.lng, coordinate.lat];
  } else {
    coord = coordinate;
  }
  const bounds = mbBounds(coord, zoom, viewport, 512);

  return {
    sw: {
      lng: bounds[0],
      lat: bounds[1],
    },
    ne: {
      lng: bounds[2],
      lat: bounds[3],
    },
  };
}

/**
 * Get a camera based on a center coordinate and a zoom factor
 * @param zoom Defaults to a zoom factor of 13.5
 */
export function centerCoordinateToCamera(
  coordinate: number[] | Types["GeoLocation"],
  zoom = DEFAULT_ZOOM
): MapboxCamera {
  const center = resolveLocation(coordinate);
  const bounds = centerCoordinateToBounds(center);
  return {
    bounds,
    center,
    zoom,
  };
}

const CENTER_COORD = { lng: 74.46086138779447, lat: 8.82308882610819 };

export const DEFAULT_COORD = { lng: 5.12142, lat: 52.090736 };

// Whole world view is the default camera
export const DEFAULT_GEO_CAMERA: MapboxCamera = centerCoordinateToCamera(
  CENTER_COORD,
  0.1
);

export function resolveCamera(
  options: (
    | MapboxCamera
    | Types["GeoLocation"]
    | (() => MapboxCamera | undefined)
    | undefined
  )[] = []
): MapboxCamera {
  for (const c of options) {
    if (!c) continue;
    if (typeof c === "function") {
      const result = c();
      if (result) return result;
    } else if ("lng" in c) return centerCoordinateToCamera(c);
    else return c;
  }

  return DEFAULT_GEO_CAMERA;
}

export function resolveViewport(
  bounds: Types["GeoBounds"],
  viewPort = DEFAULT_VIEWPORT
) {
  return viewport(
    [bounds.sw.lng, bounds.sw.lat, bounds.ne.lng, bounds.ne.lat],
    viewPort,
    0,
    19,
    512,
    true
  );
}

/**
 * Convert radians to degrees
 */
export function rad2deg(rad: number) {
  return rad * (180 / Math.PI);
}

/**
 * Convert degrees to radians
 */
export function deg2rad(degrees: number) {
  return degrees * (Math.PI / 180);
}

/**
 * Calculate the bearing as a degree between 0 and 360
 */
export function bearing(
  { lat: lat1, lng: lng1 }: Types["GeoLocation"],
  { lat: lat2, lng: lng2 }: Types["GeoLocation"]
): number {
  const dLon = lng2 - lng1;
  const y = Math.sin(dLon) * Math.cos(lat2);
  const x =
    Math.cos(lat1) * Math.sin(lat2) -
    Math.sin(lat1) * Math.cos(lat2) * Math.cos(dLon);
  const bearing = rad2deg(Math.atan2(y, x));
  return 360 - ((bearing + 360) % 360);
}

/**
 * Convert a GeoLocation type object to [lng, lat] array
 */
export function toGeolocation(lngLat: LngLat): Types["GeoLocation"] {
  return {
    lng: lngLat[0],
    lat: lngLat[1],
  };
}

/**
 * Convert a GeoBounds object to [[neLng, neLat], [swLng, swLat]] array.
 */
export function convertVisibleBounds(
  bounds?: Types["GeoBounds"]
): [LngLat, LngLat] | undefined {
  if (!bounds) return undefined;
  return [resolveCoordinate(bounds.ne), resolveCoordinate(bounds.sw)];
}

/**
 * Convert a GeoBounds object to [[neLng, neLat], [swLng, swLat]] array.
 */
export function convertCameraBounds(
  bounds?: Types["GeoBounds"],
  increaseWithFactor?: number
): CameraBounds;
export function convertCameraBounds(
  bounds?: Types["GeoBounds"],
  increaseWithFactor = 0
): CameraBounds | undefined {
  if (!bounds) return undefined;

  if (increaseWithFactor !== 0) {
    const latDiff = Math.abs(bounds.ne.lat - bounds.sw.lat) / 2;
    const lonDiff = Math.abs(bounds.ne.lng - bounds.sw.lng) / 2;

    return {
      ne: [
        bounds.ne.lng + lonDiff * increaseWithFactor,
        bounds.ne.lat + latDiff * increaseWithFactor,
      ],
      sw: [
        bounds.sw.lng - lonDiff * increaseWithFactor,
        bounds.sw.lat - latDiff * increaseWithFactor,
      ],
    };
  }

  return { ne: resolveCoordinate(bounds.ne), sw: resolveCoordinate(bounds.sw) };
}

/**
 * Get distance between two given points in meters
 */
export function distanceInMeters(
  one: number[] | Types["GeoLocation"],
  two: number[] | Types["GeoLocation"]
) {
  const [lng1, lat1] = resolveCoordinate(one);
  const [lng2, lat2] = resolveCoordinate(two);

  const R = 6371; // Radius of the earth in km
  const dLat = deg2rad(lat2 - lat1); // deg2rad below
  const dLon = deg2rad(lng2 - lng1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(deg2rad(lat1)) *
      Math.cos(deg2rad(lat2)) *
      Math.sin(dLon / 2) *
      Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  const d = R * c; // Distance in km
  return d * 1000; // Distance in m
}

const WGS84_A = 6378137.0; // Major semiaxis [m]
const WGS84_B = 6356752.3; // Minor semiaxis [m]

function getEarthRadiusForLat(lat: number) {
  const An = WGS84_A * WGS84_A * Math.cos(lat);
  const Bn = WGS84_B * WGS84_B * Math.sin(lat);
  const Ad = WGS84_A * Math.cos(lat);
  const Bd = WGS84_B * Math.sin(lat);
  return Math.sqrt((An * An + Bn * Bn) / (Ad * Ad + Bd * Bd));
}

export function getBoundingBoxOfPoint(
  location: Location,
  radiusInMeters: number
): Types["GeoBounds"] {
  const { lat, lng } = resolveLocation(location);
  const latRad = deg2rad(lat);
  const lngRad = deg2rad(lng);

  const earthRadius = getEarthRadiusForLat(latRad);
  const parallelRadius = earthRadius * Math.cos(latRad);

  return {
    sw: {
      lat: rad2deg(latRad - radiusInMeters / earthRadius),
      lng: rad2deg(lngRad - radiusInMeters / parallelRadius),
    },
    ne: {
      lat: rad2deg(latRad + radiusInMeters / earthRadius),
      lng: rad2deg(lngRad + radiusInMeters / parallelRadius),
    },
  };
}

export const getPointShape = (location: Types["GeoLocation"]): PointFeature => {
  const pointShape: PointFeature = {
    type: "Point",
    coordinates: resolveCoordinate(location),
  };
  return pointShape;
};

export function convertBoundsOrDefault(
  value?: Types["GeoBounds"]
): [LngLat, LngLat] | undefined {
  const bounds = value && convertVisibleBounds(value);
  return bounds ? bounds : undefined;
}

export function shortenCoord(coord: number, places = 5) {
  const amount = Math.pow(10, places);
  return Math.round(coord * amount) / amount;
}

export function centerOfCoordinates(coordinates: [LngLat, LngLat]): LngLat {
  const ne = coordinates[0];
  const sw = coordinates[1];
  return [(ne[0] + sw[0]) / 2, (ne[1] + sw[1]) / 2];
}

export function centerOfBounds(value?: Types["GeoBounds"]): LngLat | undefined {
  const bounds = convertBoundsOrDefault(value);
  if (!bounds) return undefined;
  return centerOfCoordinates(bounds);
}

export function coordinatesToLocation(coords: number[]): Types["GeoLocation"] {
  return {
    lng: coords[0],
    lat: coords[1],
  };
}

interface MapLike {
  // Using this interface to prevent the whole of mapboxgl to be included
  queryRenderedFeatures: (param1: any, param2: { layers: string[] }) => any[];
}

export function geoLocationOfMapviewLayer(
  map: MapLike,
  layerId: string
): Types["GeoLocation"] | undefined {
  try {
    const geom = map.queryRenderedFeatures(undefined, { layers: [layerId] })[0]
      .geometry as any;
    if (!geom.coordinates) {
      return undefined;
    }
    return coordinatesToLocation(geom.coordinates);
  } catch (e) {
    console.error(e);
    return undefined;
  }
}

export function resolveBoundingBox(
  ...locations: Location[]
): Types["GeoBounds"] {
  const lats: number[] = [];
  const lngs: number[] = [];

  for (const location of locations) {
    const { lat, lng } = resolveLocation(location);
    lats.push(lat);
    lngs.push(lng);
  }

  return {
    sw: {
      lat: Math.min(...lats),
      lng: Math.min(...lngs),
    },
    ne: {
      lat: Math.max(...lats),
      lng: Math.max(...lngs),
    },
  };
}
